import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatServerDateShort } from '@/lib/dates';

/**
 * LockLostNotice — replaces the editor the moment the working copy stops being yours: someone
 * took it over, an admin force-unlocked it, or it was submitted from another tab. Says who and
 * when, and that anything since the last save is gone, so nobody discovers it in a review.
 */
export function LockLostNotice({
  refId, reason, owner, at, nowHeldBy = null, isAdmin, onForceUnlock, onCheckout, busy = false,
}: {
  refId: string;
  reason: 'taken_over' | 'unlocked' | 'in_review';
  owner: string | null;
  at: string | null;
  /** After an unlock, who has since checked out a fresh copy (no fresh check-out offered then). */
  nowHeldBy?: string | null;
  isAdmin: boolean;
  onForceUnlock: () => void;
  onCheckout: () => void;
  busy?: boolean;
}) {
  const who = owner ?? 'Another editor';
  const when = at ? ` at ${formatServerDateShort(at)}` : '';
  const headline =
    reason === 'taken_over' ? `${who} took over this working copy${when}.`
    : reason === 'unlocked' ? 'This working copy was unlocked.'
    : 'This working copy was submitted for review.';
  const body =
    reason === 'in_review'
      ? 'It is now with the reviewer and can no longer be edited here.'
      : `Any changes since your last save were not kept.${nowHeldBy ? ` ${nowHeldBy} has since checked it out.` : ''}`;

  return (
    <div className="rounded-2xl border border-line bg-white px-6 py-16 text-center shadow-card">
      <p className="font-medium text-navy">{headline}</p>
      <p className="mx-auto mt-1 max-w-md text-[13px] text-muted">{body}</p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        <Button asChild variant="outline">
          <Link to="/vault/document/$ref" params={{ ref: refId }}>
            <ArrowLeft className="size-4" aria-hidden /> Back to document
          </Link>
        </Button>
        {reason === 'unlocked' && !nowHeldBy && (
          <Button onClick={onCheckout} disabled={busy}>Check out to edit</Button>
        )}
        {reason === 'taken_over' && isAdmin && (
          <Button variant="outline" onClick={onForceUnlock} disabled={busy}>Force unlock</Button>
        )}
      </div>
    </div>
  );
}
